import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectBasePriceTypeSliceData,
    setBasePriceType,
} from '../../../../../app/Slices/BasePriceType';
import { CustomButton } from '../../../../../layout/styledcomponents/CustomButton';
import FormMultiAutocomplete from '../../../../shared/FormMultiAutocomplete';
import FormSelect from '../../../../shared/FormSelect';
import FormTextField from '../../../../shared/FormTextField';
import { ExperienceCharge } from '../../../../types/experience';
import CalendarView from './CalendarView';
import PricingForm from './PricingForm';
import { useFormState } from '../../Form/useFormState';
import {
    ExperiencePricingSchema,
    ModulePricingSchema,
} from '../../Form/Pricing/schema';
import { z } from 'zod';

export type Pricing = {
    prices: Array<ExperienceCharge>;
};

export type SubmittedPricing = {
    charges: ExperienceCharge[];
};

interface Props {
    price: any;
    onSubmit: (data: any) => void;
    experienceType?: string;
    availableSpaces?: any;
    open: boolean;
    onClose: () => void;
    isEditing?: boolean;
}

enum FeeType {
    Fixed = 'fixed',
    PerHour = 'perHour',
    PerDay = 'perDay',
    PerNight = 'perNight',
    PerHourPerGuest = 'perHourPerGuest',
    PerDayPerGuest = 'perDayPerGuest',
    PerNightPerGuest = 'perNightPerGuest',
    PerDeskPerDay = 'perDeskPerDay',
    PerOfficePerDay = 'perOfficePerDay',
    PerMeetingRoomPerDay = 'perMeetingRoomPerDay',
    EntireVenuePerDay = 'entireVenuePerDay',
    EntireVenuePerNight = 'entireVenuePerNight',
}

export const getType = (feeId: string): string => {
    switch (feeId) {
        case 'priceDesk':
            return 'perDeskPerDay';
        case 'priceOffice':
            return 'perOfficePerDay';
        case 'priceMeetingRoom':
            return 'perMeetingRoomPerDay';
        case 'priceEntireVenue':
            return 'entireVenuePerDay';
        default:
            return 'perDay';
    }
};

function isBasic(
    p: any,
): p is { chargeType: 'price'; type: string; unitPrice: number } {
    return p?.chargeType === 'price' && 'unitPrice' in p;
}

const PricingModal = ({
    price,
    experienceType,
    open,
    onClose,
    onSubmit,
}: Props) => {
    const { experience } = useFormState();
    const { control: parentControl } =
        useFormContext<z.infer<typeof ExperiencePricingSchema>>();

    const dispatch = useDispatch();
    const showBasicPricingRedux = useSelector(selectBasePriceTypeSliceData);
    const actualPrice = price[0];
    const shouldDefaultToBasic =
        actualPrice?.chargeType === 'price' ||
        (!!actualPrice?.unitPrice && actualPrice.unitPrice > 0);

    const [showBasicPricing, setShowBasicPricing] = useState(
        shouldDefaultToBasic || showBasicPricingRedux,
    );

    const defaultFlexibleValues = {
        chargeType: 'flexiblePrice',
        dayPrice: null,
        hourPrice: null,
        nightPrice: null,
        weekendPrice: null,
        nightCheckin: '14:00',
        nightCheckout: '10:00',
        dayCheckin: '08:00',
        dayCheckout: '20:00',
    };

    const defaultBasicValues = {
        chargeType: 'price',
        type: 'perDay',
        unitPrice: 0,
        weekendPrice: null,
    };

    const methods = useForm<any>({
        defaultValues: isBasic(actualPrice)
            ? { ...defaultBasicValues, ...actualPrice }
            : { ...defaultFlexibleValues, ...actualPrice },
        resolver: zodResolver(ModulePricingSchema),
    });
    const handlePricingToggle = (useBasicPricing: boolean) => {
        setShowBasicPricing(useBasicPricing);
    };

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { isDirty },
    } = methods;

    const chargeType = watch('chargeType');
    const overrideType =
        watch('priceOverrides')?.length &&
        watch('priceOverrides')[0]?.nightPrice
            ? 'flexiblePrice'
            : 'price';

    const shouldRemoveOverrides =
        chargeType !== overrideType && watch('priceOverrides')?.length > 0;

    const [cancel, setCancel] = useState<boolean>(false);
    const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
    const handleCancel = () => {
        if (isDirty && !cancel) {
            setCancel(true);
            return;
        }
        if (cancel) reset();
        setCancel(false);
        setConfirmSubmit(false);
        onClose();
    };

    useEffect(() => {
        if (!price) return;

        const actual = price[0];

        const shouldShowBasic =
            actual?.chargeType === 'price' ||
            (price?.prices && Array.isArray(price.prices));

        setShowBasicPricing(shouldShowBasic);
        dispatch(setBasePriceType(shouldShowBasic));
    }, [price, open]);

    const basicWeekendPrice = useWatch({
        control,
        name: 'weekendPrice',
    });

    const handleClearWeekendPrice = () => {
        setValue('weekendPrice.unitPrice', 0);
        setValue('weekendPrice.days', []);
    };

    const handleCheck = () => {
        if (basicWeekendPrice !== null) {
            setValue('weekendPrice', null);
        } else {
            setValue('weekendPrice.unitPrice', 0);
            setValue('weekendPrice.days', []);
        }
    };

    const handleSubmitData = (data: any) => {
        const isFlexibleForm = data.chargeType === 'flexiblePrice';

        const payload = isFlexibleForm
            ? {
                  chargeType: 'flexiblePrice',
                  dayPrice: data.dayPrice ?? null,
                  hourPrice: data.hourPrice ?? null,
                  nightPrice: data.nightPrice ?? null,
                  weekendPrice: data.weekendPrice ?? null,
                  nightCheckin: data.nightCheckin ?? null,
                  nightCheckout: data.nightCheckout ?? null,
                  dayCheckin: data.dayCheckin ?? null,
                  dayCheckout: data.dayCheckout ?? null,
                  startDate: data.startDate ?? null,
                  endDate: data.endDate ?? null,
                  checkinTime: data.checkinTime ?? null,
                  checkoutTime: data.checkoutTime ?? null,
                  priceOverrides: data.priceOverrides || [],
              }
            : {
                  chargeType: 'price',
                  type: data.type,
                  unitPrice: data.unitPrice,
                  weekendPrice: data.weekendPrice ?? null,
                  startDate: data.startDate ?? null,
                  endDate: data.endDate ?? null,
                  checkinTime: data.checkinTime ?? null,
                  checkoutTime: data.checkoutTime ?? null,
                  priceOverrides: data.priceOverrides || [],
              };

        setConfirmSubmit(false);
        onSubmit(payload);
        onClose();
    };

    const onNestedSubmitBasicPrice: React.FormEventHandler<HTMLFormElement> = (
        event,
    ) => {
        if (event?.stopPropagation) event.stopPropagation();
        if (shouldRemoveOverrides && !confirmSubmit) {
            event.preventDefault();
            setConfirmSubmit(true);
            return;
        }
        if (shouldRemoveOverrides) {
            setValue('priceOverrides', []);
        }

        return handleSubmit(handleSubmitData)(event);
    };

    const onNestedSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        if (event?.stopPropagation) event.stopPropagation();
        if (shouldRemoveOverrides && !confirmSubmit) {
            event.preventDefault();
            setConfirmSubmit(true);
            return;
        }
        if (shouldRemoveOverrides) {
            setValue('priceOverrides', []);
        }

        return handleSubmit(handleSubmitData)(event);
    };

    useEffect(() => {
        methods.register('chargeType');
    }, [methods]);

    return (
        <>
            {showBasicPricing ? (
                <Dialog
                    open={open}
                    onClose={onClose}
                    fullWidth
                    maxWidth='md'
                    PaperProps={{
                        sx: {
                            width: '100%',
                            maxWidth: '720px',
                            overflow: 'visible',
                        },
                    }}
                >
                    <FormProvider {...methods}>
                        <form onSubmit={onNestedSubmitBasicPrice}>
                            {/* Header Row */}
                            <DialogTitle
                                component='div'
                                sx={{
                                    alignItems: 'flex-start',
                                    gap: 2,
                                    pb: 0,
                                }}
                            >
                                {experienceType !== 'commercial' && (
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: { xs: 1, md: 14 },
                                                minWidth: 0,
                                            }}
                                        >
                                            Basic Pricing
                                            <FormControlLabel
                                                label={
                                                    showBasicPricing
                                                        ? 'Basic Pricing: On'
                                                        : 'Basic Pricing: Off'
                                                }
                                                control={
                                                    <Switch
                                                        checked={
                                                            showBasicPricing
                                                        }
                                                        onChange={(e) => {
                                                            const checked =
                                                                e.target
                                                                    .checked;
                                                            setShowBasicPricing(
                                                                checked,
                                                            );
                                                            setValue(
                                                                'chargeType',
                                                                checked
                                                                    ? 'price'
                                                                    : 'flexiblePrice',
                                                            );
                                                            handlePricingToggle(
                                                                checked,
                                                            );
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    mr: 0,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            fontSize='12px'
                                            sx={{ my: 1, fontWeight: 400 }}
                                        >
                                            Applied to all dates unless you
                                            customise. <strong>Note:</strong>{' '}
                                            all prices are excluding GST.
                                        </Typography>
                                    </Box>
                                )}
                                <IconButton
                                    onClick={handleCancel}
                                    size='small'
                                    aria-label='close'
                                    sx={{ color: 'text.primary', ml: 'auto' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>

                            {/* CONTENT */}
                            <DialogContent>
                                {/* Amount + Type */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel
                                            required
                                            htmlFor='base-price-amount'
                                        >
                                            Amount
                                        </InputLabel>
                                        <FormTextField
                                            id='base-price-amount'
                                            name='unitPrice'
                                            type='number'
                                            control={control}
                                            inputProps={{
                                                'aria-label':
                                                    'Base price amount',
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position='start'>
                                                        <Typography>
                                                            $
                                                        </Typography>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel
                                            required
                                            id='base-price-type-label'
                                            htmlFor='base-price-type'
                                        >
                                            Type
                                        </InputLabel>
                                        <FormSelect
                                            id='base-price-type'
                                            labelId='base-price-type-label'
                                            aria-label='Base price type'
                                            name='type'
                                            control={control}
                                            options={[
                                                {
                                                    value: FeeType.PerNight,
                                                    label: 'Per Night',
                                                },
                                                {
                                                    value: FeeType.PerDay,
                                                    label: 'Per Day',
                                                },
                                                {
                                                    value: FeeType.PerNightPerGuest,
                                                    label: 'Per Night Per Guest',
                                                },
                                                {
                                                    value: FeeType.PerDayPerGuest,
                                                    label: 'Per Day Per Guest',
                                                },
                                                {
                                                    value: FeeType.PerHour,
                                                    label: 'Per Hour',
                                                },
                                                {
                                                    value: FeeType.PerHourPerGuest,
                                                    label: 'Per Hour Per Guest',
                                                },
                                            ]}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Custom Weekend Toggle */}
                                <Box
                                    display='flex'
                                    justifyContent='space-between'
                                    alignItems='center'
                                    mt={3}
                                >
                                    <Stack spacing={0.5}>
                                        <Typography fontWeight='bold'>
                                            Custom Pricing
                                        </Typography>
                                        <Typography fontSize='12px'>
                                            Add a custom weekend price
                                        </Typography>
                                    </Stack>
                                    <Checkbox
                                        inputProps={{
                                            'aria-label':
                                                'Add custom weekend price',
                                        }}
                                        checked={!!basicWeekendPrice}
                                        onChange={handleCheck}
                                    />
                                </Box>

                                {/* Weekend Price Fields */}
                                {basicWeekendPrice !== null && (
                                    <>
                                        <FormHelperText sx={{ mt: 1, mb: 2 }}>
                                            Base price will be overridden by the
                                            weekend price on the days you
                                            select.
                                        </FormHelperText>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <InputLabel htmlFor='weekend-price-amount'>
                                                    Amount
                                                </InputLabel>
                                                <FormTextField
                                                    id='weekend-price-amount'
                                                    name='weekendPrice.unitPrice'
                                                    control={control}
                                                    type='number'
                                                    inputProps={{
                                                        'aria-label':
                                                            'Weekend amount',
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position='start'>
                                                                <Typography>
                                                                    $
                                                                </Typography>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <InputLabel htmlFor='weekend-price-days'>
                                                    Days
                                                </InputLabel>
                                                <FormMultiAutocomplete
                                                    id='weekend-price-days'
                                                    name='weekendPrice.days'
                                                    control={control}
                                                    options={[
                                                        {
                                                            value: 'friday',
                                                            label: 'Friday',
                                                        },
                                                        {
                                                            value: 'saturday',
                                                            label: 'Saturday',
                                                        },
                                                        {
                                                            value: 'sunday',
                                                            label: 'Sunday',
                                                        },
                                                    ]}
                                                />
                                            </Grid>
                                        </Grid>
                                    </>
                                )}
                                <Collapse in={confirmSubmit}>
                                    <Alert severity='error' sx={{ mt: 2 }}>
                                        <strong>Warning:</strong> Switching
                                        pricing types will remove{' '}
                                        <strong>
                                            {watch('priceOverrides')?.length ||
                                                0}{' '}
                                            custom price override(s)
                                        </strong>
                                        . Click <strong>Save</strong> again to
                                        confirm.
                                    </Alert>
                                </Collapse>
                            </DialogContent>

                            {/* ACTIONS */}
                            <DialogActions sx={{ px: 3, py: 2 }}>
                                <Box
                                    display='flex'
                                    flexDirection={isMobile ? 'column' : 'row'}
                                    gap={2}
                                    width='100%'
                                    justifyContent={
                                        isMobile ? 'initial' : 'space-between'
                                    }
                                >
                                    {experienceType !== 'commercial' && (
                                        <CustomButton
                                            width={isMobile ? '100%' : 'auto'}
                                            variant='contained'
                                            color='neutral'
                                            onClick={handleClearWeekendPrice}
                                        >
                                            Clear Custom Price
                                        </CustomButton>
                                    )}

                                    <Box
                                        display='flex'
                                        flexDirection={
                                            isMobile ? 'column' : 'row'
                                        }
                                        gap={2}
                                        width={isMobile ? '100%' : 'auto'}
                                    >
                                        <CustomButton
                                            width={isMobile ? '100%' : 'auto'}
                                            variant='contained'
                                            color='neutral'
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </CustomButton>
                                        <CustomButton
                                            width={isMobile ? '100%' : 'auto'}
                                            variant='contained'
                                            color='primary'
                                            type='submit'
                                            autoFocus
                                        >
                                            Save
                                        </CustomButton>
                                    </Box>
                                </Box>
                            </DialogActions>
                        </form>
                    </FormProvider>
                </Dialog>
            ) : (
                <Dialog
                    open={open}
                    onClose={handleCancel}
                    maxWidth='md'
                    fullWidth
                >
                    <FormProvider {...methods}>
                        <form onSubmit={onNestedSubmit}>
                            <DialogTitle component='div' sx={{ gap: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: { xs: 1, md: 14 },
                                        minWidth: 0,
                                    }}
                                >
                                    Flexible Pricing
                                    <FormControlLabel
                                        label={
                                            showBasicPricing
                                                ? 'Basic Pricing: On'
                                                : 'Basic Pricing: Off'
                                        }
                                        checked={showBasicPricing}
                                        control={
                                            <Switch
                                                checked={showBasicPricing}
                                                onChange={(e) => {
                                                    const checked =
                                                        e.target.checked;

                                                    setShowBasicPricing(
                                                        checked,
                                                    );
                                                    setConfirmSubmit(false);
                                                    setValue(
                                                        'chargeType',
                                                        checked
                                                            ? 'price'
                                                            : 'flexiblePrice',
                                                    );
                                                    handlePricingToggle(
                                                        checked,
                                                    );
                                                }}
                                            />
                                        }
                                        sx={{ mr: 0, flexShrink: 0 }}
                                    />
                                </Box>
                                <IconButton
                                    onClick={handleCancel}
                                    size='small'
                                    aria-label='close'
                                    sx={{ color: 'text.primary' }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>

                            <DialogContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={6}>
                                        <PricingForm />
                                    </Grid>
                                    <Grid item xs={12} lg={6}>
                                        <CalendarView
                                            experience={experience}
                                            parentControl={parentControl}
                                        />
                                    </Grid>
                                </Grid>
                                <Collapse in={cancel}>
                                    <Alert severity='warning' sx={{ mt: 2 }}>
                                        You have unsaved changes. Are you sure
                                        you want to cancel?
                                    </Alert>
                                </Collapse>
                                <Collapse in={confirmSubmit}>
                                    <Alert severity='error' sx={{ mt: 2 }}>
                                        <strong>Warning:</strong> Switching
                                        pricing types will remove{' '}
                                        <strong>
                                            {watch('priceOverrides')?.length ||
                                                0}{' '}
                                            custom price override(s)
                                        </strong>
                                        . Click <strong>Save</strong> again to
                                        confirm.
                                    </Alert>
                                </Collapse>
                            </DialogContent>
                            <DialogActions>
                                <CustomButton
                                    onClick={handleCancel}
                                    variant='contained'
                                    color='neutral'
                                >
                                    Cancel
                                </CustomButton>
                                <Button variant='contained' type='submit'>
                                    Save
                                </Button>
                            </DialogActions>
                        </form>
                    </FormProvider>
                </Dialog>
            )}
        </>
    );
};

export default PricingModal;
